"use client";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

const CLEAR = "__clear__";
// 12-hour clock: hours 12, 1–11 (12 first so it maps to 00/12 cleanly), plus
// quarter-hour minutes and an AM/PM meridiem. The stored value stays 24-hour.
const HOURS_12 = ["12", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11"];
const MINUTES = ["00", "15", "30", "45"];
const MERIDIEMS = ["AM", "PM"];

interface Decomposed {
  hour12: string;
  minute: string;
  meridiem: string;
}

/** Split a stored 24-hour "09:30[:00]" into 12-hour parts; junk → all empty. */
function decompose(value: string | null): Decomposed {
  if (!value) return { hour12: "", minute: "", meridiem: "" };
  const m = /^(\d{2}):(\d{2})/.exec(value);
  if (!m) return { hour12: "", minute: "", meridiem: "" };
  const h24 = Number(m[1]);
  const minute = m[2] ?? "";
  const meridiem = h24 < 12 ? "AM" : "PM";
  const hour12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return { hour12: String(hour12), minute, meridiem };
}

/** Recombine 12-hour parts into a stored 24-hour "HH:MM". Missing parts default. */
function compose(hour12: string, minute: string, meridiem: string): string {
  const h = Number(hour12 || "12");
  const m = minute || "00";
  let h24 = h % 12; // 12 → 0
  if ((meridiem || "AM") === "PM") h24 += 12;
  return `${String(h24).padStart(2, "0")}:${m}`;
}

interface TimeSelectProps {
  value: string | null;
  /** Emits 24-hour "HH:MM", or "" when cleared. */
  onChange: (value: string) => void;
  placeholder?: string;
  /** Show a row that clears the value (the field is optional). */
  clearable?: boolean;
  clearLabel?: string;
  disabled?: boolean;
  className?: string;
  "aria-label"?: string;
  "aria-invalid"?: boolean;
}

/**
 * Three side-by-side list boxes for a 12-hour time: hour (12, 1–11), minute
 * (00/15/30/45), and AM/PM. Consumes/emits 24-hour "HH:MM" so it stays a
 * drop-in for the field and the stored value is unchanged. Picking one box
 * defaults the others to a valid time (12:00 AM); the optional clear row on the
 * hour/minute boxes resets to empty.
 */
export function TimeSelect({
  value,
  onChange,
  clearable = true,
  clearLabel = "—",
  disabled,
  className,
  "aria-label": ariaLabel,
  "aria-invalid": ariaInvalid,
}: TimeSelectProps) {
  const { hour12, minute, meridiem } = decompose(value);
  // Keep an off-grid stored minute (e.g. legacy "09:07") visible as an option.
  const minuteOptions = minute && !MINUTES.includes(minute) ? [...MINUTES, minute] : MINUTES;

  return (
    <div className={cn("flex items-center gap-1", className)}>
      <Select
        value={hour12 || undefined}
        onValueChange={(h) => onChange(h === CLEAR ? "" : compose(h, minute, meridiem))}
        disabled={disabled}
      >
        <SelectTrigger className="flex-1" aria-label={ariaLabel} aria-invalid={ariaInvalid}>
          <SelectValue placeholder="Hr" />
        </SelectTrigger>
        <SelectContent>
          {clearable ? (
            <SelectItem value={CLEAR}>
              <span className="text-muted-foreground">{clearLabel}</span>
            </SelectItem>
          ) : null}
          {HOURS_12.map((h) => (
            <SelectItem key={h} value={h}>
              {h}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <span className="text-muted-foreground">:</span>
      <Select
        value={minute || undefined}
        onValueChange={(m) => onChange(m === CLEAR ? "" : compose(hour12, m, meridiem))}
        disabled={disabled}
      >
        <SelectTrigger className="flex-1" aria-invalid={ariaInvalid}>
          <SelectValue placeholder="Min" />
        </SelectTrigger>
        <SelectContent>
          {clearable ? (
            <SelectItem value={CLEAR}>
              <span className="text-muted-foreground">{clearLabel}</span>
            </SelectItem>
          ) : null}
          {minuteOptions.map((m) => (
            <SelectItem key={m} value={m}>
              {m}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div
        className={cn(
          "flex h-9 shrink-0 overflow-hidden rounded-md border",
          ariaInvalid ? "border-destructive" : "border-input",
        )}
      >
        {MERIDIEMS.map((p) => {
          const active = meridiem === p;
          return (
            <button
              key={p}
              type="button"
              disabled={disabled}
              aria-pressed={active}
              onClick={() => onChange(compose(hour12, minute, p))}
              className={cn(
                "px-2.5 text-xs font-medium transition-colors disabled:pointer-events-none disabled:opacity-50",
                "[&:not(:first-child)]:border-l [&:not(:first-child)]:border-input",
                active
                  ? "bg-primary text-primary-foreground"
                  : "bg-card text-foreground hover:bg-muted/50",
              )}
            >
              {p}
            </button>
          );
        })}
      </div>
    </div>
  );
}
